/**
 * Main CustomerCard Component for Sales Check-in
 * Handles Customer Search, Creation, Editing
 * Uses internal CustomerCardState machine for state management
 */

// react
import React, { Suspense, useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

// components
import { CustomerSearch } from '../CustomerManagement/CustomerSearch';
import { CustomerDetails } from '../CustomerManagement/CustomerDetails';
import { EditCustomer } from '../CustomerManagement/EditCustomer';
import { CustomerInformationCard } from './CustomerInformationCard';
import SalesCheckInSpinner from '../SalesCheckInSpinner/SalesCheckInSpinner';

// utilities

// types
import type { CommonConsumer } from '@makemydeal/dr-platform-types';
import { CustomerCardConsumerInitialState, backdropExcludeClassesForSlideoutClose } from '../../constants';
import {
    cancelUpdateCustomer,
    setCoBuyer,
    useCustomerCardState,
    useCustomerSelector,
    useDealerDetailsSelector,
    useFormattedMessageSelector
} from '@makemydeal/dr-sales-checkin-store';
import { CustomerCardState, DeepLinks } from '@makemydeal/dr-sales-checkin-types';
import { getSlideoutTitle, isValidCustomer } from '@makemydeal/dr-sales-checkin-ui-utils';
import { StyledSlideout } from './CustomerInformationExperience.style';

const CustomerInformationExperience: React.FC = () => {
    // states
    const [customerDetails, setCustomerDetails] = useState<CommonConsumer>(() => CustomerCardConsumerInitialState);
    const [showSearch, setShowSearch] = useState(false);

    // Hooks
    const dispatch = useDispatch();
    const { checkedInCustomer, coBuyer, isCoBuyer } = useCustomerSelector();
    const dealerDetails = useDealerDetailsSelector();
    const { state, options, transitionTo, handleCloseSlider } = useCustomerCardState();
    const commonOrgId = dealerDetails?.commonOrgId || '';
    const inboxConsumerId = (isCoBuyer ? coBuyer?.consumerId : checkedInCustomer?.consumerId) ?? '';
    const formattedMessage = useFormattedMessageSelector(options?.message ?? DeepLinks.Invalid);

    // useEffects
    useEffect(() => {
        if (checkedInCustomer) {
            setCustomerDetails(checkedInCustomer);
        }
    }, [checkedInCustomer]);

    // Internal Methods
    const handleSelectCustomer = useCallback(
        (customer: CommonConsumer) => {
            setCustomerDetails(customer);
            if (isValidCustomer(customer) && isCoBuyer) {
                dispatch(setCoBuyer(customer));
                transitionTo(CustomerCardState.CLOSE);
            } else {
                transitionTo(CustomerCardState.EDIT);
            }
        },
        [dispatch, isCoBuyer, transitionTo]
    );

    const handleCustomerSave = useCallback(
        (customer?: CommonConsumer) => {
            // If the customer is not saved, navigate back to the search state
            if (customer == null) {
                dispatch(cancelUpdateCustomer());
                return transitionTo(CustomerCardState.SEARCH);
            }

            if (isCoBuyer) {
                dispatch(setCoBuyer(customer));
                transitionTo(CustomerCardState.CLOSE);
            }
        },
        [dispatch, isCoBuyer, transitionTo]
    );

    useEffect(() => {
        if (state !== CustomerCardState.CLOSE) {
            setShowSearch(true);
            return;
        }

        setShowSearch(false);
    }, [state]);

    const renderContent = () => {
        switch (state) {
            case CustomerCardState.LOADING:
                return <SalesCheckInSpinner />;
            case CustomerCardState.DETAILS:
                return (
                    <CustomerInformationCard
                        customer={isCoBuyer ? 'coBuyer' : 'buyer'}
                        heading={getSlideoutTitle(isCoBuyer, state)}
                        onClose={handleCloseSlider}
                        saveLabel="Save"
                        syncSelectedCustomer
                        showSaveLoader
                    />
                );
            case CustomerCardState.INBOX:
                return (
                    <CustomerDetails
                        commonConsumerId={inboxConsumerId}
                        setShow={handleCloseSlider}
                        messageContentSelector={() => formattedMessage}
                    />
                );
            case CustomerCardState.EDIT:
                return <EditCustomer commonConsumer={customerDetails} onCustomerSave={handleCustomerSave} />;
            case CustomerCardState.SEARCH:
                return <CustomerSearch commonOrgId={commonOrgId} onCustomerSelected={handleSelectCustomer} />;

            default:
                return null;
        }
    };

    return (
        <StyledSlideout
            show={showSearch}
            title={getSlideoutTitle(isCoBuyer, state)}
            onClose={handleCloseSlider}
            // ui-bricks - Slideout takes dataTestId prop to assign the raw data-testid attribute.
            dataTestId="customer-details-drawer"
            backdropExcludeClasses={backdropExcludeClassesForSlideoutClose}
            $isLoading={state === CustomerCardState.LOADING}
            $detailsView={state === CustomerCardState.DETAILS}
        >
            <Suspense fallback={<SalesCheckInSpinner />}>{renderContent()}</Suspense>
        </StyledSlideout>
    );
};

export default CustomerInformationExperience;
